import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Storage,
  Description,
  Business,
  Assessment,
  Refresh,
} from '@mui/icons-material';
import { DatabaseInfo } from '../types/api';
import { apiCall } from '../utils/api';

const DatabasePage: React.FC = () => {
  const [dbInfo, setDbInfo] = useState<DatabaseInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    fetchDatabaseInfo();
  }, []);

  const fetchDatabaseInfo = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiCall('/api/database-info');
      
      if (!response.ok) {
        throw new Error('Failed to fetch database information');
      }
      
      const data = await response.json();
      setDbInfo(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching database info:', error);
      setError('Failed to connect to database');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDatabaseInfo();
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading database information...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Database Monitor
          </Typography>
        </Box>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Database Monitor
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Overview of stored documentation and regulation data.
          </Typography>
        </Box>
        <Box>
          <Chip
            icon={<Refresh />}
            label="Refresh"
            onClick={handleRefresh}
            clickable
            variant="outlined"
          />
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      </Box>

      {dbInfo && (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Storage color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Database Statistics
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Description sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h4" color="primary.main">
                      {dbInfo.documentation_count.toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      Documentation Points
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                    <Assessment sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                    <Typography variant="h4" color="secondary.main">
                      {dbInfo.regulation_count.toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                      Regulation Points
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Business color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Clients ({dbInfo.clients.length})
                  </Typography>
                </Box>
                
                {dbInfo.clients.length > 0 ? (
                  <List dense>
                    {dbInfo.clients.map((client, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={client} />
                        <Chip
                          label="Active"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No clients found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Description color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Documents ({dbInfo.documents.length})
                  </Typography>
                </Box>
                
                {dbInfo.documents.length > 0 ? (
                  <List dense>
                    {dbInfo.documents.map((document, index) => (
                      <ListItem key={index}>
                        <ListItemText 
                          primary={document}
                          secondary={`Document ${index + 1}`}
                        />
                        <Chip
                          label="Processed"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No documents found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Database Health
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Chip
              label="Connected"
              color="success"
              variant="outlined"
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Database Connection
            </Typography>
          </Box>
          
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Chip
              label="Active"
              color="success"
              variant="outlined"
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Vector Search
            </Typography>
          </Box>
          
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Chip
              label="Optimal"
              color="success"
              variant="outlined"
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Performance
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default DatabasePage;